import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RotateCw, RotateCcw, RefreshCw, Loader2, Sun, Contrast } from "lucide-react";

const ASPECTS = [
  { label: "1:1", value: 1 },
  { label: "4:5", value: 0.8 },
  { label: "3:4", value: 0.75 },
];

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (e) => reject(e));
    image.src = url;
  });
}

async function getEditedBlob(imageSrc, crop, rotation, brightness, contrast) {
  const image = await createImage(imageSrc);
  const rad = (rotation * Math.PI) / 180;
  const bW = Math.abs(Math.cos(rad) * image.width) + Math.abs(Math.sin(rad) * image.height);
  const bH = Math.abs(Math.sin(rad) * image.width) + Math.abs(Math.cos(rad) * image.height);

  const canvas = document.createElement("canvas");
  canvas.width = bW;
  canvas.height = bH;
  const ctx = canvas.getContext("2d");
  // Exposure only — brightness & contrast. No hue/saturation/white-balance change.
  ctx.filter = `brightness(${brightness}) contrast(${contrast})`;
  ctx.translate(bW / 2, bH / 2);
  ctx.rotate(rad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  const out = document.createElement("canvas");
  out.width = Math.round(crop.width);
  out.height = Math.round(crop.height);
  const octx = out.getContext("2d");
  octx.drawImage(canvas, crop.x, crop.y, crop.width, crop.height, 0, 0, crop.width, crop.height);

  return new Promise((resolve) => out.toBlob((b) => resolve(b), "image/jpeg", 0.92));
}

export default function LiquorPhotoEditor({ open, onOpenChange, imageUrl, onApply, title = "Crop & exposure", defaultAspect = 1, aspects = ASPECTS }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1);
  const [aspect, setAspect] = useState(defaultAspect);
  const [pixels, setPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_area, areaPixels) => setPixels(areaPixels), []);

  const reset = () => {
    setZoom(1);
    setRotation(0);
    setBrightness(1);
    setContrast(1);
    setCrop({ x: 0, y: 0 });
  };

  const apply = async () => {
    if (!pixels) return;
    setBusy(true);
    try {
      const blob = await getEditedBlob(imageUrl, pixels, rotation, brightness, contrast);
      const file = new File([blob], "liquor-edited.jpg", { type: "image/jpeg" });
      await onApply(file);
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  const cssFilter = `brightness(${brightness}) contrast(${contrast})`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B3B2E]">{title}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-[#14513D]/70 -mt-2">
          Only crop, rotation and exposure (brightness/contrast) are applied — the natural tea colour is never altered.
        </p>

        <div className="relative w-full h-[340px] rounded-lg overflow-hidden bg-[#0B3B2E]" data-testid="liquor-cropper">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            style={{ mediaStyle: { filter: cssFilter } }}
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <Label className="text-xs font-semibold text-[#14513D]">Aspect</Label>
            <div className="mt-2 flex gap-2">
              {aspects.map((a) => (
                <Button key={a.label} size="sm" variant={aspect === a.value ? "default" : "outline"} onClick={() => setAspect(a.value)} data-testid={`liquor-aspect-${a.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  {a.label}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#14513D]">Rotate · {rotation}°</Label>
            <div className="mt-2 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setRotation((r) => (r - 90 + 360) % 360)} data-testid="liquor-rotate-left"><RotateCcw className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setRotation((r) => (r + 90) % 360)} data-testid="liquor-rotate-right"><RotateCw className="h-4 w-4" /></Button>
              <Slider className="flex-1" value={[rotation]} min={0} max={360} step={1} onValueChange={(v) => setRotation(v[0])} data-testid="liquor-rotation" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#14513D]">Zoom · {zoom.toFixed(2)}×</Label>
            <Slider className="mt-2" value={[zoom]} min={1} max={3} step={0.02} onValueChange={(v) => setZoom(v[0])} data-testid="liquor-zoom" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#14513D] flex items-center gap-1"><Sun className="h-3.5 w-3.5" /> Brightness · {brightness.toFixed(2)}</Label>
            <Slider className="mt-2" value={[brightness]} min={0.6} max={1.5} step={0.01} onValueChange={(v) => setBrightness(v[0])} data-testid="liquor-brightness" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-[#14513D] flex items-center gap-1"><Contrast className="h-3.5 w-3.5" /> Contrast · {contrast.toFixed(2)}</Label>
            <Slider className="mt-2" value={[contrast]} min={0.6} max={1.5} step={0.01} onValueChange={(v) => setContrast(v[0])} data-testid="liquor-contrast" />
          </div>
          <div className="flex items-end">
            <Button variant="ghost" onClick={reset} data-testid="liquor-reset" className="gap-1"><RefreshCw className="h-4 w-4" /> Reset</Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="liquor-edit-cancel">Cancel</Button>
          <Button onClick={apply} disabled={busy || !pixels} className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2" data-testid="liquor-edit-apply">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Apply changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
